import { useState, useEffect } from 'react';
import { IconRefresh, IconLoader2, IconBan, IconX } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

type CampaignSmsJobStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'COMPLETED_WITH_FAILURES'
  | 'CANCELLED';

interface JobSummary {
  jobId: string;
  /** 'SMS' for legacy campaign_sms_jobs rows, which predate the column. */
  channel?: string;
  campaignId: string;
  status: CampaignSmsJobStatus;
  recipientCount: number;
  createdAt: string;
  updatedAt: string;
  pendingCount: number;
  processingCount: number;
  submittedCount: number;
  failedCount: number;
  blockedCount: number;
  cancelledCount: number;
  /**
   * PUSH recipients Nova reported as having no live device subscription.
   * Neither delivered nor failed — the consent-only audience cannot see the
   * subscription table, so this is an expected outcome of every push send.
   * Absent on legacy SMS rows, whose queue has no such status.
   */
  skippedCount?: number;
}

const CANCELLABLE_STATUSES: CampaignSmsJobStatus[] = ['QUEUED', 'RUNNING'];

/** Legacy rows carry no channel; the backend labels them SMS, and this is the
 * belt-and-braces fallback for a response that predates that. */
function ChannelBadge({ channel }: { channel?: string }) {
  return <Badge variant="outline">{channel || 'SMS'}</Badge>;
}

const getApiErrorMessage = (err: any, fallback: string): string => (
  err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback
);

function StatusBadge({ status }: { status: CampaignSmsJobStatus }) {
  switch (status) {
    case 'COMPLETED':
      return <Badge variant="success">{status}</Badge>;
    case 'COMPLETED_WITH_FAILURES':
      return <Badge variant="destructive">{status}</Badge>;
    case 'CANCELLED':
      return <Badge variant="warning">{status}</Badge>;
    case 'QUEUED':
    case 'RUNNING':
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

/** Confirmation dialog for cancelling a QUEUED/RUNNING job. Renders nothing when no job is targeted. */
function CancelJobDialog({
  job,
  cancelling,
  cancelError,
  onConfirm,
  onClose,
}: {
  job: JobSummary | null;
  cancelling: boolean;
  cancelError: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const outstandingCount = job ? job.pendingCount + job.processingCount : 0;

  return (
    <Dialog open={!!job} onOpenChange={(open) => { if (!open && !cancelling) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cancel Send Job</DialogTitle>
          <DialogDescription>Are you sure you want to cancel this campaign send job?</DialogDescription>
        </DialogHeader>

        {job && (
          <div className="space-y-4 py-2">
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div>
                <p className="text-sm font-medium">Campaign:</p>
                <p className="text-sm">{job.campaignId}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Outstanding recipients:</p>
                <p className="text-sm">{outstandingCount.toLocaleString()}</p>
              </div>
            </div>
            <Alert variant="warning">
              <AlertDescription>
                Not-yet-claimed recipients will be dropped, but up to ~20 in-flight messages may
                already be sending and could still be delivered.
              </AlertDescription>
            </Alert>
            {cancelError && (
              <Alert variant="destructive">
                <AlertDescription>{cancelError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={cancelling}>
            Back
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={cancelling}>
            {cancelling ? <IconLoader2 size={16} className="animate-spin" /> : <IconBan size={16} />}
            {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CampaignJobsPage() {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cancelTarget, setCancelTarget] = useState<JobSummary | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const loadJobs = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.get('/campaigns/jobs');
      setJobs(response.data.jobs || []);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load campaign send jobs'));
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const closeCancelDialog = () => {
    setCancelTarget(null);
    setCancelError('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError('');
    try {
      const response = await api.post(`/campaigns/jobs/${encodeURIComponent(cancelTarget.jobId)}/cancel`);
      const cancelledRecipientCount = response.data?.cancelledRecipientCount ?? 0;
      setSuccess(
        `Cancelled ${cancelledRecipientCount} pending recipient${cancelledRecipientCount !== 1 ? 's' : ''} for job ${cancelTarget.jobId}.`,
      );
      closeCancelDialog();
      await loadJobs();
    } catch (err: any) {
      setCancelError(getApiErrorMessage(err, 'Failed to cancel campaign send job'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Campaign Send Jobs"
        subtitle="View durable send jobs across every channel and cancel one that is still queued or running."
      />

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <Button onClick={loadJobs} disabled={loading}>
              <IconRefresh className="mr-2 h-4 w-4" />
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="icon-xs" onClick={() => setError('')}><IconX size={14} /></Button>
              </AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {success}
                <Button variant="ghost" size="icon-xs" onClick={() => setSuccess('')}><IconX size={14} /></Button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center p-8">
          <IconLoader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {!loading && jobs.length > 0 && (
        <MainCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">Job ID</TableHead>
                <TableHead className="font-bold">Channel</TableHead>
                <TableHead className="font-bold">Campaign</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="text-right font-bold">Recipients</TableHead>
                <TableHead className="text-right font-bold">Submitted</TableHead>
                <TableHead className="text-right font-bold">Pending</TableHead>
                <TableHead className="text-right font-bold" title="Push recipients with no active device subscription. Not a failure — there was nowhere to deliver.">Skipped</TableHead>
                <TableHead className="text-right font-bold">Failed</TableHead>
                <TableHead className="text-right font-bold">Blocked</TableHead>
                <TableHead className="text-right font-bold">Cancelled</TableHead>
                <TableHead className="font-bold">Created</TableHead>
                <TableHead className="font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.jobId}>
                  <TableCell title={job.jobId} className="font-mono text-xs">
                    {job.jobId.slice(0, 8)}
                  </TableCell>
                  <TableCell><ChannelBadge channel={job.channel} /></TableCell>
                  <TableCell>{job.campaignId}</TableCell>
                  <TableCell><StatusBadge status={job.status} /></TableCell>
                  <TableCell className="text-right">{job.recipientCount.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{job.submittedCount.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{(job.pendingCount + job.processingCount).toLocaleString()}</TableCell>
                  <TableCell className="text-right">{(job.skippedCount ?? 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">{job.failedCount.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{job.blockedCount.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{job.cancelledCount.toLocaleString()}</TableCell>
                  <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    {CANCELLABLE_STATUSES.includes(job.status) && (
                      <Button variant="outline" size="sm" className="text-destructive" onClick={() => setCancelTarget(job)}>
                        <IconBan size={14} />
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MainCard>
      )}

      {!loading && jobs.length === 0 && !error && (
        <MainCard className="text-center">
          <p className="text-muted-foreground">
            No campaign send jobs found. Send a campaign to see durable jobs here.
          </p>
        </MainCard>
      )}

      <CancelJobDialog
        job={cancelTarget}
        cancelling={cancelling}
        cancelError={cancelError}
        onConfirm={handleConfirmCancel}
        onClose={closeCancelDialog}
      />
    </div>
  );
}
